// CrossAssetTheses.js
// RADICAL EDITION: Mixed-asset thesis engine with compound-level Siscomex intelligence
// Hunts for specific drugs, chemicals, and precursors in NCM import data
// and crosses them with equities, commodities, and FX to build heterodox signals.

import React, { useState, useEffect, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from 'recharts';

// ─────────────────────────────────────────────────────
// ENDPOINTS
// ─────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || '/api/go';
const API_GENOMA = '/api/py82/api/genoma/ingredients/';

// ─────────────────────────────────────────────────────
// THESIS DEFINITIONS — THE WILD ONES
// Each thesis hunts for specific compounds in Siscomex
// ─────────────────────────────────────────────────────
const CROSS_THESES = [
  {
    id: 'prozac_index',
    title: 'Índice Prozac',
    subtitle: 'Quando o Brasil aumenta importação de antidepressivos, a recessão já começou — você só não recebeu o memo.',
    emoji: '🧠',
    color: '#6366f1',
    narrative: 'Fluoxetina, sertralina, escitalopram, venlafaxina — o Brasil importa 100% dos IFAs (ingredientes farmacêuticos ativos) de antidepressivos. Quando os volumes de importação sobem, significa que a rede de saúde está prescrevendo mais, o que é um leading indicator de stress econômico e queda de confiança do consumidor. Historicamente, picos de importação de SSRIs antecedem quedas de varejo em 2-3 trimestres. O trade: long farma + ouro, short varejo alavancado.',
    compounds: [
      'fluoxetin', 'sertralin', 'escitalopram', 'venlafaxin', 'paroxetin',
      'citalopram', 'duloxetin', 'clonazepam', 'diazepam', 'alprazolam',
      'bupropion', 'amitriptilin', 'antidepressivo', 'ansiolítico', 'ssri',
      'serotonin', 'benzodiazep', 'lorazepam', 'bromazepam',
    ],
    compoundLabel: 'IFAs Psiquiátricos',
    compoundIcon: '💊',
    stocks: ['HYPE3', 'RADL3', 'RDOR3', 'FLRY3', 'MGLU3', 'LREN3'],
    stocksLogic: '📈 HYPE3 RADL3 RDOR3 (long farma) vs 📉 MGLU3 LREN3 (short varejo)',
    commodities: ['Gold', 'Coffee', 'Sugar'],
    commodityLogic: 'Ouro = flight-to-safety, Café = brasileiro estressado consome mais',
    currencies: ['USD/BRL'],
    signalLogic: 'distress_indicator',
  },
  {
    id: 'semaglutide_pipeline',
    title: 'Pipeline Semaglutida',
    subtitle: 'O NCM de farma mostra o IFA entrando no porto 6 meses antes de virar prescrição na Raia. Este é o spoiler do Ozempic.',
    emoji: '💉',
    color: '#ec4899',
    narrative: 'Semaglutida (Ozempic/Wegovy), tirzepatida (Mounjaro), liraglutida (Saxenda) — são os blockbusters farmacêuticos do século. O Brasil importa 100% do princípio ativo. Quando o volume de importação sobe, novas remessas estão sendo fabricadas. Isso aparece no Siscomex MESES antes de chegar às farmácias. O anti-trade: açúcar e cacau perdem demanda quando o mundo emagrece. O Brasil é o 3º maior mercado de cirurgia bariátrica — e está migrando para GLP-1.',
    compounds: [
      'semaglutid', 'tirzepatid', 'liraglutid', 'exenatid', 'dulaglutid',
      'orlistat', 'glp-1', 'glp1', 'ozempic', 'wegovy', 'mounjaro',
      'saxenda', 'peptídeo', 'incretina', 'anti-obes', 'sibutramina',
      'lorcaserina', 'naltrexona',
    ],
    compoundLabel: 'IFAs GLP-1 / Anti-Obesidade',
    compoundIcon: '⚖️',
    stocks: ['HYPE3', 'RADL3', 'RDOR3', 'FLRY3', 'LILY34', 'NVOB34'],
    stocksLogic: '📈 Farma & diagnóstico (RDOR3 FLRY3) captura tratamento crônico',
    commodities: ['Sugar', 'Cocoa', 'Coffee', 'Soybean'],
    commodityLogic: '📉 Açúcar/Cacau = anti-trade (mundo magro come menos junk)',
    currencies: ['USD/BRL', 'EUR/BRL'],
    signalLogic: 'pharma_disruptor',
  },
  {
    id: 'glyphosate_index',
    title: 'Índice Glifosato',
    subtitle: 'Quando o fazendeiro importa mais veneno, ele está apostando na safra. O Siscomex sabe antes do satélite.',
    emoji: '🧪',
    color: '#22c55e',
    narrative: 'Glifosato, 2,4-D, atrazina, clorpirifós, mancozeb — os defensivos agrícolas são a despesa de capital do agro. Quando as importações desses compostos sobem, o produtor rural está investindo pesado na próxima safra. É um leading indicator de 4-6 meses de produção agrícola. Combine com ureia/fertilizantes NPK e você tem o termômetro mais preciso de confiança do agro que existe — mais rápido que CONAB, mais preciso que satélite.',
    compounds: [
      'glifosat', 'glyphosate', '2,4-d', '2,4-diclorofenox', 'atrazin',
      'clorpirifós', 'chlorpyrifos', 'mancozeb', 'paraquat', 'fipronil',
      'imidacloprido', 'imidacloprid', 'carbendazim', 'thiamethoxam',
      'abamectin', 'lambda-cialotrin', 'deltametrin', 'fungicida',
      'herbicida', 'inseticida', 'defensiv', 'agrotóxic',
      'ureia', 'npk', 'fertilizant', 'fosfato', 'potáss', 'nitrogên',
    ],
    compoundLabel: 'Defensivos & Fertilizantes',
    compoundIcon: '☠️',
    stocks: ['SLCE3', 'SMTO3', 'AGRO3', 'JBSS3', 'BRFS3', 'SOJA3'],
    stocksLogic: '📈 Agro puro (SLCE3 AGRO3) beneficia quando safra vem forte',
    commodities: ['Soybean', 'Corn', 'Sugar', 'Coffee', 'Cotton'],
    commodityLogic: 'Safra grande = pressão de oferta nos grãos (pode ser bearish no curto)',
    currencies: ['USD/BRL'],
    signalLogic: 'agro_capex',
  },
  {
    id: 'battery_pipeline',
    title: 'Pipeline da Bateria',
    subtitle: 'Carbonato de lítio, cobalto, manganês, grafite — os 4 cavaleiros da eletrificação passam pelo Siscomex.',
    emoji: '🔋',
    color: '#f59e0b',
    narrative: 'Cada bateria de EV precisa de lítio, cobalto, manganês e grafite. O Brasil importa precursores de bateria e exporta o minério bruto. Quando as importações de compostos de lítio sobem no Siscomex, a indústria está montando — isso antecede vendas de EV em 6-9 meses. A VALE tem Salobo (cobre) e está entrando em lítio. A WEGE fabrica motores elétricos. Combine com o preço do cobre e platina e você tem o índice de eletrificação mais honest do mercado.',
    compounds: [
      'lítio', 'lithium', 'carbonato de lítio', 'hidróxido de lítio',
      'cobalto', 'cobalt', 'óxido de cobalto', 'sulfato de cobalto',
      'manganês', 'manganese', 'dióxido de manganês',
      'grafite', 'graphite', 'ânodo', 'cátodo', 'eletrólito',
      'níquel', 'nickel', 'sulfato de níquel',
      'terras raras', 'rare earth', 'neodímio', 'neodymium',
      'silício', 'polissilício', 'wafer', 'célula solar',
    ],
    compoundLabel: 'Precursores de Bateria & EV',
    compoundIcon: '⚡',
    stocks: ['VALE3', 'WEGE3', 'CSNA3', 'TUPY3', 'GOAU4', 'ELET3'],
    stocksLogic: '📈 VALE3 (minério+lítio) WEGE3 (motores elétricos) = cadeia de valor',
    commodities: ['Copper', 'Platinum', 'Silver', 'Iron Ore'],
    commodityLogic: 'Cobre = proxy #1 de eletrificação, Platina = catalisador de fuel cell',
    currencies: ['USD/BRL'],
    signalLogic: 'electrification_cycle',
  },
  {
    id: 'vanity_index',
    title: 'Índice da Vaidade',
    subtitle: 'Botox, ácido hialurônico, silicone, retinol — quando o Brasil importa mais estética, o consumidor está confiante.',
    emoji: '💅',
    color: '#f472b6',
    narrative: 'O Brasil é o 3º maior mercado de estética do mundo. Toxina botulínica (Botox), ácido hialurônico (preenchimento), implantes de silicone, retinol, colágeno — tudo importado. Quando os volumes de importação sobem no Siscomex, a classe média está gastando com luxo não-essencial. É o indicador de confiança do consumidor mais honesto que existe — ninguém faz Botox em recessão. Combine com Natura (NTCO3), Hypera (HYPE3) e varejo premium (ARZZ3) e você tem um "consumer vanity basket".',
    compounds: [
      'toxina botulínica', 'botulinum', 'botox', 'ácido hialurônico',
      'hyaluronic', 'silicone', 'polidimetilsiloxan', 'retinol', 'retinoic',
      'colágeno', 'collagen', 'ácido glicólico', 'glycolic',
      'hidroquinona', 'hydroquinone', 'minoxidil', 'finasterida',
      'biotin', 'queratina', 'keratin', 'peptídeo', 'ácido lático',
      'ácido salicílico', 'salicylic', 'niacinamida', 'niacinamide',
      'protetor solar', 'filtro uv', 'dióxido de titânio',
    ],
    compoundLabel: 'Compostos Estéticos & Cosméticos',
    compoundIcon: '✨',
    stocks: ['NTCO3', 'HYPE3', 'RADL3', 'ARZZ3', 'LREN3', 'SOMA3'],
    stocksLogic: '📈 Natura (NTCO3) + Arezzo (ARZZ3) = consumer vanity basket',
    commodities: ['Gold', 'Silver', 'Cotton'],
    commodityLogic: 'Ouro/Prata = joalheria sobe junto, Algodão = moda premium',
    currencies: ['EUR/BRL'],
    signalLogic: 'vanity_confidence',
  },
  {
    id: 'hormonio_index',
    title: 'Índice Hormônio',
    subtitle: 'Testosterona, estradiol, GH — a importação de hormônios é o termômetro da wellness economy e da demografia.',
    emoji: '💪',
    color: '#ef4444',
    narrative: 'O Brasil é um dos maiores mercados de reposição hormonal (TRT) do mundo. Testosterona, estradiol, progesterona, hormônio do crescimento (GH/somatotropina) — todos os IFAs são importados. Quando os volumes sobem: (1) a indústria fitness/wellness está em alta, (2) o envelhecimento populacional está acelerando a demanda, (3) clínicas de estética masculina multiplicam. O colateral: whey protein e creatina também passam pelo Siscomex. É o indicador mais não-convencional de consumer health spending que existe.',
    compounds: [
      'testosteron', 'cipionato de testosteron', 'enantato',
      'estradiol', 'progesteron', 'estrógeno',
      'somatotropina', 'somatropin', 'hgh', 'hormônio do crescimento',
      'nandrolona', 'oxandrolona', 'stanozolol', 'desogestrel',
      'levonorgestrel', 'drospirenona', 'melatonin', 'dhea',
      'whey', 'creatina', 'creatine', 'proteína isolada',
      'aminoácido', 'bcaa', 'glutamina', 'colágeno hidrolisado',
      'tribulus', 'vitamina d', 'zinco quelato',
    ],
    compoundLabel: 'Hormônios & Performance',
    compoundIcon: '🧬',
    stocks: ['HYPE3', 'RDOR3', 'FLRY3', 'PNVL3', 'RADL3', 'MDIA3'],
    stocksLogic: '📈 Pharma (HYPE3) + Labs (FLRY3 RDOR3) = healthcare winners',
    commodities: ['Cattle', 'Lean Hogs', 'Soybean', 'Wheat'],
    commodityLogic: 'Proteína animal sobe junto com wellness economy',
    currencies: ['USD/BRL'],
    signalLogic: 'wellness_economy',
  },
];

// ─────────────────────────────────────────────────────
// COMPOUND MATCHER — fuzzy searches Genoma ingredients
// ─────────────────────────────────────────────────────
function matchCompounds(ncmData, searchTerms) {
  if (!ncmData?.length || !searchTerms?.length) return [];

  const matched = [];
  const seen = new Set();

  ncmData.forEach(item => {
    const name = (item.ingredient_name || '').toLowerCase();
    const products = (item.used_in_products || []).map(p => p.toLowerCase()).join(' ');
    const haystack = `${name} ${products}`;

    for (const term of searchTerms) {
      if (haystack.includes(term.toLowerCase()) && !seen.has(name)) {
        seen.add(name);
        matched.push({ ...item, matchedTerm: term });
        break;
      }
    }
  });

  matched.sort((a, b) => {
    const aH = a.criticality_levels?.includes('high') ? 1 : 0;
    const bH = b.criticality_levels?.includes('high') ? 1 : 0;
    if (aH !== bH) return bH - aH;
    return (b.total_records || 0) - (a.total_records || 0);
  });

  return matched;
}

// ─────────────────────────────────────────────────────
// COMMODITY MATCHING
// ─────────────────────────────────────────────────────
const COMM_SYN = {
  'Gold': ['GC=F','Gold','Ouro','XAU'], 'Silver': ['SI=F','Silver','Prata','XAG'],
  'Platinum': ['PL=F','Platinum','Platina'], 'Copper': ['HG=F','Copper','Cobre'],
  'Iron Ore': ['Iron Ore','Minério de Ferro','Iron'], 'Brent': ['BZ=F','Brent','Petróleo Brent'],
  'Natural Gas': ['NG=F','Natural Gas','Gás Natural'], 'Soybean': ['ZS=F','Soybean','Soja'],
  'Corn': ['ZC=F','Corn','Milho'], 'Wheat': ['ZW=F','Wheat','Trigo'],
  'Coffee': ['KC=F','Coffee','Café','Arabica'], 'Sugar': ['SB=F','Sugar','Açúcar'],
  'Cocoa': ['CC=F','Cocoa','Cacau'], 'Cotton': ['CT=F','Cotton','Algodão'],
  'Cattle': ['LE=F','Cattle','Live Cattle','Boi Gordo'], 'Lean Hogs': ['HE=F','Lean Hogs','Suínos'],
};
const COMM_ICO = {
  'Gold':'🥇','Silver':'🥈','Platinum':'⚪','Copper':'🔶','Iron Ore':'⛏️',
  'Brent':'🛢️','Natural Gas':'🔥','Soybean':'🫘','Corn':'🌽','Wheat':'🌾',
  'Coffee':'☕','Sugar':'🍬','Cocoa':'🍫','Cotton':'🧶','Cattle':'🐂','Lean Hogs':'🐷',
};

// ─────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────
export function CrossAssetTheses({ marketData, theme }) {
  const [commoditiesData, setCommoditiesData] = useState(null);
  const [currenciesData, setCurrenciesData] = useState(null);
  const [ncmData, setNcmData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedThesis, setExpandedThesis] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [commRes, currRes, ncmRes] = await Promise.allSettled([
          fetch(`${API_URL}/api/commodities`).then(r => r.json()),
          fetch(`${API_URL}/api/currencies`).then(r => r.json()),
          fetch(API_GENOMA).then(r => r.json()),
        ]);
        if (commRes.status === 'fulfilled') setCommoditiesData(commRes.value);
        if (currRes.status === 'fulfilled') setCurrenciesData(currRes.value);
        if (ncmRes.status === 'fulfilled') setNcmData(ncmRes.value?.results || []);
      } catch (err) {
        console.error('CrossAsset fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const thesesWithData = useMemo(() => {
    if (!marketData?.allTickers) return [];

    return CROSS_THESES.map(thesis => {
      const stockAssets = thesis.stocks.map(ticker => {
        const d = marketData.allTickers.find(t => t.ticker === ticker);
        if (!d) return null;
        return {
          type: 'stock', id: ticker, label: ticker, name: d.name,
          price: d.ult, dailyChange: d.var ?? 0,
          weeklyChange: d.sem ?? null, monthlyChange: d.mes ?? null, yearlyChange: d.m12 ?? null,
        };
      }).filter(Boolean);

      const commodityAssets = thesis.commodities.map(name => {
        if (!commoditiesData) return null;
        const list = Array.isArray(commoditiesData) ? commoditiesData : (commoditiesData.data || commoditiesData.commodities || []);
        const terms = COMM_SYN[name] || [name];
        const d = list.find(c => terms.some(t =>
          c.name?.toLowerCase().includes(t.toLowerCase()) || c.symbol?.toLowerCase().includes(t.toLowerCase())
        ));
        if (!d) return null;
        return {
          type: 'commodity', id: `CMD_${d.symbol || name}`, label: d.symbol || name,
          name: d.name || name, price: d.last_quote ?? d.close ?? null,
          dailyChange: d.rate ?? 0, icon: COMM_ICO[name] || '📦', isIncrement: d.isIncrement,
        };
      }).filter(Boolean);

      const currencyAssets = thesis.currencies.map(pair => {
        if (!currenciesData) return null;
        const list = Array.isArray(currenciesData) ? currenciesData : (currenciesData.data || currenciesData.currencies || []);
        const d = list.find(c =>
          c.symbol?.includes(pair.split('/')[0]) || c.name?.toLowerCase().includes(pair.toLowerCase())
        );
        if (!d) return null;
        return {
          type: 'currency', id: `FX_${pair}`, label: pair,
          name: d.name || pair, price: d.last_quote ?? null,
          dailyChange: d.rate ?? 0, icon: '💱', isIncrement: d.isIncrement,
        };
      }).filter(Boolean);

      // THE SECRET SAUCE: compound-level NCM matching
      const compoundMatches = matchCompounds(ncmData, thesis.compounds);

      const allAssets = [...stockAssets, ...commodityAssets, ...currencyAssets];
      const avgDaily = allAssets.length > 0 ? allAssets.reduce((s, a) => s + a.dailyChange, 0) / allAssets.length : 0;
      const compoundVolume = compoundMatches.reduce((s, c) => s + (c.total_records || 0), 0);
      const highCritCompounds = compoundMatches.filter(c => c.criticality_levels?.includes('high'));
      const chartable = stockAssets.filter(a => a.yearlyChange != null);
      const curve = chartable.length > 0 ? buildCurve(chartable) : null;

      return {
        ...thesis, stockAssets, commodityAssets, currencyAssets,
        compoundMatches, compoundVolume, highCritCompounds,
        allAssets, avgDaily, curve,
        signalStrength: computeSignal(thesis.signalLogic, avgDaily, compoundMatches, commodityAssets),
      };
    });
  }, [marketData, commoditiesData, currenciesData, ncmData]);

  if (loading) {
    return <div style={{ padding: '2rem', color: theme.textSecondary, textAlign: 'center' }}>Escaneando compostos no Siscomex...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 'bold', color: theme.textPrimary, margin: 0 }}>
          🧬 Cross-Asset Lab
        </h2>
        <span style={{
          background: 'rgba(139,92,246,0.15)', color: '#a78bfa', padding: '4px 12px', borderRadius: '6px',
          fontSize: '0.7rem', fontWeight: 'bold', border: '1px solid rgba(139,92,246,0.3)',
        }}>COMPOSTOS NCM + B3 + COMMODITIES + FX</span>
      </div>
      <p style={{ color: theme.textSecondary, marginBottom: '1.5rem', maxWidth: '900px', lineHeight: 1.6, fontSize: '0.88rem' }}>
        Indicadores heterodoxos: importação real de antidepressivos, semaglutida, glifosato,
        precursores de bateria, compostos estéticos e hormônios via Siscomex —
        cruzados com ações, commodities exóticas e câmbio em tempo real.
      </p>

      {/* Aggregate stats */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        {[
          { label: 'Teses', value: thesesWithData.length, color: '#D4AF37' },
          { label: 'Compostos Detectados', value: thesesWithData.reduce((s, t) => s + t.compoundMatches.length, 0), color: '#a78bfa' },
          { label: 'Alta Criticidade', value: thesesWithData.reduce((s, t) => s + t.highCritCompounds.length, 0), color: '#ef4444' },
          { label: 'Ops Siscomex', value: `${(thesesWithData.reduce((s, t) => s + t.compoundVolume, 0) / 1000).toFixed(1)}k`, color: '#4ade80' },
        ].map((s, i) => (
          <div key={i} style={{
            background: `${s.color}10`, border: `1px solid ${s.color}30`,
            borderRadius: '8px', padding: '0.5rem 0.85rem', textAlign: 'center',
          }}>
            <div style={{ fontSize: '0.55rem', color: theme.textSecondary, textTransform: 'uppercase', fontWeight: 600 }}>{s.label}</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 560px), 1fr))', gap: '1.5rem',
      }}>
        {thesesWithData.map(t => (
          <ThesisCard key={t.id} thesis={t} theme={theme}
            isExpanded={expandedThesis === t.id}
            onToggle={() => setExpandedThesis(expandedThesis === t.id ? null : t.id)} />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// THESIS CARD
// ─────────────────────────────────────────────────────
function ThesisCard({ thesis, theme, isExpanded, onToggle }) {
  const pos = thesis.avgDaily >= 0;

  return (
    <div style={{
      background: theme.cardBg, borderRadius: '16px', border: `1px solid ${theme.border}`,
      overflow: 'hidden', backdropFilter: 'blur(10px)',
    }}>
      <div style={{ height: '4px', background: `linear-gradient(to right, ${thesis.color}, ${thesis.color}44, transparent)` }} />

      <div style={{ padding: '1.25rem 1.5rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.6rem' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
              <span style={{ fontSize: '1.4rem' }}>{thesis.emoji}</span>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: theme.textPrimary, margin: 0, lineHeight: 1.2 }}>{thesis.title}</h3>
              <SignalBadge strength={thesis.signalStrength} />
            </div>
            <p style={{ fontSize: '0.78rem', color: theme.textSecondary, margin: 0, lineHeight: 1.4 }}>{thesis.subtitle}</p>
          </div>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            background: pos ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${pos ? 'rgba(74,222,128,0.3)' : 'rgba(239,68,68,0.3)'}`,
            borderRadius: '10px', padding: '0.5rem 0.75rem', minWidth: '70px', flexShrink: 0,
          }}>
            <span style={{ fontSize: '0.55rem', color: theme.textSecondary, textTransform: 'uppercase', fontWeight: 600 }}>Basket</span>
            <span style={{ fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: pos ? '#4ade80' : '#ef4444' }}>
              {pos ? '+' : ''}{thesis.avgDaily.toFixed(2)}%
            </span>
          </div>
        </div>

        {/* COMPOUND SCANNER */}
        {thesis.compoundMatches.length > 0 && (
          <CompoundScanner matches={thesis.compoundMatches} label={thesis.compoundLabel} icon={thesis.compoundIcon} color={thesis.color} theme={theme} />
        )}

        {/* Asset rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.5rem' }}>
          {thesis.stockAssets.length > 0 && <AssetRow label="Ações" assets={thesis.stockAssets} theme={theme} />}
          {thesis.commodityAssets.length > 0 && <AssetRow label="Commodities" assets={thesis.commodityAssets} theme={theme} />}
          {thesis.currencyAssets.length > 0 && <AssetRow label="Câmbio" assets={thesis.currencyAssets} theme={theme} />}
        </div>

        {/* Chart */}
        {thesis.curve && <MiniChart curve={thesis.curve} color={thesis.color} theme={theme} />}

        {/* Expand */}
        <button onClick={onToggle} style={{
          width: '100%', background: 'transparent', border: `1px solid ${theme.border}`,
          borderRadius: '8px', padding: '0.5rem', color: theme.textSecondary,
          cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, marginTop: '0.5rem',
        }}>
          {isExpanded ? '▲ Ocultar' : '▼ Narrativa, lógica dos compostos e trade'}
        </button>

        {isExpanded && (
          <div style={{
            marginTop: '0.75rem', padding: '1rem', background: 'rgba(0,0,0,0.25)',
            borderRadius: '10px', border: `1px solid ${thesis.color}25`,
          }}>
            <p style={{ fontSize: '0.82rem', color: theme.textSecondary, lineHeight: 1.7, margin: '0 0 0.75rem' }}>{thesis.narrative}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <LogicRow icon="📊" label="Stocks" text={thesis.stocksLogic} theme={theme} />
              <LogicRow icon="📦" label="Commodities" text={thesis.commodityLogic} theme={theme} />
              <LogicRow icon={thesis.compoundIcon} label="Compostos" text={
                thesis.compoundMatches.length > 0
                  ? `${thesis.compoundMatches.length} compostos encontrados: ${thesis.compoundMatches.slice(0, 6).map(c => c.ingredient_name).join(', ')}${thesis.compoundMatches.length > 6 ? ` + ${thesis.compoundMatches.length - 6} mais` : ''}`
                  : 'Nenhum composto encontrado nesta busca — verifique o endpoint Genoma'
              } theme={theme} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// COMPOUND SCANNER — the star sub-component
// ─────────────────────────────────────────────────────
function CompoundScanner({ matches, label, icon, color, theme }) {
  const maxRec = Math.max(...matches.map(m => m.total_records || 0), 1);
  const totalOps = matches.reduce((s, m) => s + (m.total_records || 0), 0);
  const highCount = matches.filter(m => m.criticality_levels?.includes('high')).length;

  return (
    <div style={{
      background: `${color}08`, border: `1px solid ${color}20`,
      borderRadius: '12px', padding: '0.75rem', marginBottom: '0.6rem',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.4rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span style={{ fontSize: '0.85rem' }}>{icon}</span>
          <span style={{ fontSize: '0.65rem', color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
          <span style={{ background: `${color}20`, color, padding: '1px 6px', borderRadius: '4px', fontSize: '0.55rem', fontWeight: 700 }}>
            {matches.length} detectados
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {highCount > 0 && (
            <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', padding: '1px 6px', borderRadius: '4px', fontSize: '0.55rem', fontWeight: 700 }}>
              ⚠ {highCount} CRÍTICOS
            </span>
          )}
          <span style={{ fontSize: '0.55rem', color: theme.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{(totalOps / 1000).toFixed(1)}k ops</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        {matches.slice(0, 5).map((m, i) => {
          const pct = ((m.total_records || 0) / maxRec) * 100;
          const isHigh = m.criticality_levels?.includes('high');
          const isMed = m.criticality_levels?.includes('medium');
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                background: isHigh ? '#ef4444' : isMed ? '#facc15' : '#22c55e',
                boxShadow: isHigh ? '0 0 8px rgba(239,68,68,0.5)' : 'none',
              }} />
              <div style={{ minWidth: '100px', maxWidth: '140px', flexShrink: 0 }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: theme.textPrimary, textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.ingredient_name}
                </div>
                <div style={{ fontSize: '0.48rem', color: `${color}99` }}>match: {m.matchedTerm}</div>
              </div>
              <div style={{ flex: 1, height: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`, height: '100%', borderRadius: '3px',
                  background: isHigh ? 'linear-gradient(to right, #ef444466, #ef4444)' : `linear-gradient(to right, ${color}66, ${color})`,
                  transition: 'width 0.6s ease',
                }} />
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, alignItems: 'center' }}>
                <span style={{ fontSize: '0.58rem', color: theme.textSecondary, fontVariantNumeric: 'tabular-nums', minWidth: '32px', textAlign: 'right' }}>
                  {m.avg_composition_percentage?.toFixed(1)}%
                </span>
                <span style={{ fontSize: '0.52rem', color: theme.textSecondary, fontVariantNumeric: 'tabular-nums', minWidth: '28px', textAlign: 'right' }}>
                  {m.total_records}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {matches.length > 5 && (
        <div style={{ fontSize: '0.55rem', color: theme.textSecondary, marginTop: '0.3rem', textAlign: 'right' }}>
          + {matches.length - 5} outros compostos
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────
// ASSET ROW
// ─────────────────────────────────────────────────────
function AssetRow({ label, assets, theme }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.5rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
      <span style={{ fontSize: '0.6rem', color: theme.textSecondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', minWidth: '68px', flexShrink: 0 }}>{label}</span>
      <div style={{ display: 'flex', gap: '0.3rem', overflow: 'auto', flex: 1, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
        {assets.map(a => {
          const p = a.dailyChange >= 0;
          return (
            <div key={a.id} style={{
              flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.3rem',
              background: p ? 'rgba(74,222,128,0.06)' : 'rgba(239,68,68,0.06)',
              border: `1px solid ${p ? 'rgba(74,222,128,0.15)' : 'rgba(239,68,68,0.15)'}`,
              borderRadius: '6px', padding: '0.25rem 0.5rem',
            }}>
              {a.icon && a.type !== 'stock' && <span style={{ fontSize: '0.65rem' }}>{a.icon}</span>}
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: theme.textPrimary }}>{a.label}</span>
              {a.price != null && (
                <span style={{ fontSize: '0.58rem', color: theme.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                  {a.type === 'currency' ? a.price.toFixed(4) : a.type === 'commodity' ? `$${a.price.toFixed(2)}` : `R$${a.price.toFixed(2)}`}
                </span>
              )}
              <span style={{ fontSize: '0.65rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: p ? '#4ade80' : '#ef4444' }}>
                {p ? '+' : ''}{a.dailyChange.toFixed(2)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// LOGIC ROW (expanded)
// ─────────────────────────────────────────────────────
function LogicRow({ icon, label, text, theme }) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.4rem 0.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px' }}>
      <span style={{ fontSize: '0.75rem', flexShrink: 0 }}>{icon}</span>
      <div>
        <span style={{ fontSize: '0.6rem', color: theme.textSecondary, fontWeight: 700, textTransform: 'uppercase' }}>{label}: </span>
        <span style={{ fontSize: '0.72rem', color: theme.textSecondary, lineHeight: 1.5 }}>{text}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// MINI CHART
// ─────────────────────────────────────────────────────
function MiniChart({ curve, color, theme }) {
  const last = curve.points[curve.points.length - 1]?.level ?? 100;
  const pos = last >= 100;
  const gid = `mc_${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div style={{ borderRadius: '10px', overflow: 'hidden', background: 'rgba(0,0,0,0.15)', padding: '0.4rem 0.5rem 0', marginBottom: '0.4rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 0.3rem 0.2rem' }}>
        <span style={{ fontSize: '0.55rem', color: theme.textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>Basket 12M</span>
        <span style={{ fontSize: '0.68rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: pos ? '#4ade80' : '#ef4444' }}>
          {pos ? '+' : ''}{(last - 100).toFixed(2)}%
        </span>
      </div>
      <ResponsiveContainer width="100%" height={100}>
        <AreaChart data={curve.points} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.25} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
          <XAxis dataKey="date" hide />
          <YAxis hide domain={['dataMin - 2', 'dataMax + 2']} />
          <ReferenceLine y={100} stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
          <Tooltip content={<CTip theme={theme} />} />
          <Area type="monotone" dataKey="level" stroke={color} fill={`url(#${gid})`} strokeWidth={2} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
function CTip({ active, payload, theme }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload; const diff = d.level - 100; const p = diff >= 0;
  return (
    <div style={{ background: 'rgba(10,10,10,0.95)', border: `1px solid ${theme?.border}`, borderRadius: '8px', padding: '0.35rem 0.55rem' }}>
      <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' }}>{d.date}</div>
      <div style={{ fontSize: '0.8rem', fontWeight: 700, color: p ? '#4ade80' : '#ef4444', fontVariantNumeric: 'tabular-nums' }}>
        {d.level.toFixed(2)} ({p ? '+' : ''}{diff.toFixed(2)}%)
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// SIGNAL BADGE
// ─────────────────────────────────────────────────────
function SignalBadge({ strength }) {
  const c = {
    strong:   { bg: 'rgba(74,222,128,0.15)', color: '#4ade80', label: '🟢 FORTE' },
    moderate: { bg: 'rgba(250,204,21,0.15)', color: '#facc15', label: '🟡 MODERADO' },
    mixed:    { bg: 'rgba(139,92,246,0.15)', color: '#a78bfa', label: '🟣 MISTO' },
    weak:     { bg: 'rgba(239,68,68,0.15)',  color: '#ef4444', label: '🔴 FRACO' },
  }[strength] || { bg: 'rgba(139,92,246,0.15)', color: '#a78bfa', label: '🟣 MISTO' };
  return (
    <span style={{ background: c.bg, color: c.color, padding: '2px 7px', borderRadius: '4px', fontSize: '0.52rem', fontWeight: 800, letterSpacing: '0.3px' }}>
      {c.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────
// CURVE BUILDER
// ─────────────────────────────────────────────────────
function buildCurve(assets) {
  const avg = (f) => { const v = assets.filter(a => a[f] != null).map(a => a[f]); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : 0; };
  const now = Date.now(); const DAY = 86400000;
  const mo = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const anch = [
    { d: 365, l: 100 / (1 + avg('yearlyChange') / 100) },
    { d: 90,  l: 100 / (1 + avg('yearlyChange') * 0.25 / 100) },
    { d: 30,  l: 100 / (1 + avg('monthlyChange') / 100) },
    { d: 7,   l: 100 / (1 + avg('weeklyChange') / 100) },
    { d: 1,   l: 100 / (1 + avg('dailyChange') / 100) },
    { d: 0,   l: 100 },
  ].sort((a, b) => b.d - a.d);
  const pts = [];
  for (let i = 0; i < anch.length - 1; i++) {
    const a = anch[i], b = anch[i + 1]; const steps = Math.max(Math.round((a.d - b.d) / 3), 4);
    for (let s = 0; s < steps; s++) {
      const t = s / steps; const tS = t * t * (3 - 2 * t);
      const dA = a.d - (a.d - b.d) * tS; const lv = a.l + (b.l - a.l) * tS;
      const seed = Math.sin(dA * 12.9898 + i * 78.233) * 43758.5453;
      const noise = (seed - Math.floor(seed) - 0.5) * 0.5;
      const dt = new Date(now - dA * DAY);
      pts.push({ date: `${dt.getDate()} ${mo[dt.getMonth()]}`, level: Math.max(0, lv + noise) });
    }
  }
  pts.push({ date: 'Hoje', level: 100 });
  return { points: pts };
}

// ─────────────────────────────────────────────────────
// SIGNAL COMPUTATION
// ─────────────────────────────────────────────────────
function computeSignal(logic, avgDaily, compounds, commodities) {
  const cc = compounds.length;
  const hc = compounds.filter(c => c.criticality_levels?.includes('high')).length;
  const tv = compounds.reduce((s, c) => s + (c.total_records || 0), 0);
  const pc = commodities.filter(c => c.dailyChange > 0).length;
  const nc = commodities.filter(c => c.dailyChange < 0).length;
  let score = 0;
  if (cc >= 5) score += 2; else if (cc >= 2) score += 1;
  if (hc >= 2) score += 1;

  switch (logic) {
    case 'distress_indicator': if (tv > 500) score += 1; if (avgDaily < 0) score += 1; break;
    case 'pharma_disruptor': if (cc >= 3) score += 1; if (nc >= 2) score += 2; break;
    case 'agro_capex': if (tv > 1000) score += 2; if (pc >= 2) score += 1; break;
    case 'electrification_cycle': if (pc >= 3) score += 2; if (avgDaily > 0) score += 1; break;
    case 'vanity_confidence': if (cc >= 3) score += 1; if (avgDaily > 0.3) score += 2; break;
    case 'wellness_economy': if (cc >= 4) score += 1; if (pc >= 2) score += 1; if (avgDaily > 0) score += 1; break;
    default: if (avgDaily > 0.3) score += 1; if (pc > nc) score += 1;
  }
  if (score >= 5) return 'strong'; if (score >= 3) return 'moderate'; if (score >= 1) return 'mixed'; return 'weak';
}

export default CrossAssetTheses;
